import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  ArrowLeft,
  ChevronRight,
  Plus,
  Minus,
  Search,
  Compass,
  Wrench,
  Activity,
  TrendingUp,
  Sparkles,
} from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import { getCategoryBySlug, APP_CATEGORY_LIST } from "../data/appCategoryData";

const PROCESS_STEPS = [
  { n: "01", icon: Search, title: "Audit", text: "Deep audit of listing, keywords, competitors, conversion funnel and creative." },
  { n: "02", icon: Compass, title: "Strategy", text: "Custom keyword stack, screenshot brief and creative direction per market." },
  { n: "03", icon: Wrench, title: "Optimize", text: "Rebuild metadata, screenshots and video. Ratings & reviews ops kick in." },
  { n: "04", icon: Activity, title: "Test", text: "A/B tests on Play Console + iOS Custom Product Pages run continuously." },
  { n: "05", icon: TrendingUp, title: "Scale", text: "Winners go live in all markets. Paid UA stacks on top of ranked organic." },
];

/* ─────────────────────────────────────────
   FAQ Accordion
───────────────────────────────────────── */
function FAQAccordion({ items }) {
  const [open, setOpen] = useState(0);
  return (
    <ul className="border-t border-zinc-200">
      {items.map((f, idx) => {
        const isOpen = open === idx;
        return (
          <li
            key={f.q}
            className="border-b border-zinc-200"
            data-testid={`category-faq-${idx}`}
          >
            <button
              onClick={() => setOpen(isOpen ? -1 : idx)}
              className="w-full py-6 flex items-center justify-between gap-6 text-left group"
              aria-expanded={isOpen}
            >
              <span className="font-display text-lg md:text-xl font-bold text-charcoal group-hover:text-electric transition-colors">
                {f.q}
              </span>
              <span
                className={`shrink-0 w-9 h-9 grid place-items-center border transition-colors ${
                  isOpen
                    ? "bg-electric border-electric text-white"
                    : "border-zinc-300 text-charcoal group-hover:border-charcoal"
                }`}
              >
                {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              </span>
            </button>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
                className="pb-6 pr-12"
              >
                <p className="text-zinc-600 leading-relaxed text-[15px]">{f.a}</p>
              </motion.div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/* ─────────────────────────────────────────
   Main Page
───────────────────────────────────────── */
export default function AppCategoryPage() {
  const { categorySlug } = useParams();
  const cat = getCategoryBySlug(categorySlug);

  if (!cat || !cat.heroHeadline) {
    return (
      <>
        <Navbar />
        <main className="pt-40 pb-40 text-center bg-white">
          <p className="text-overline text-electric mb-3">404</p>
          <h1 className="font-display text-4xl font-extrabold text-charcoal mb-6">
            Category page not found
          </h1>
          <Link
            to="/app-categories"
            className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3 font-semibold hover:bg-[#42A5F5] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> View all categories
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const related = (cat.relatedSlugs || [])
    .map((s) => APP_CATEGORY_LIST.find((c) => c.slug === s))
    .filter(Boolean);

  return (
    <>
      <Helmet>
        <title>{cat.metaTitle}</title>
        <meta name="description" content={cat.metaDescription} />
        <link rel="canonical" href={`https://lumotis.com/aso/${cat.slug}`} />
      </Helmet>

      <Navbar />

      <main data-testid={`category-page-${cat.slug}`}>

        {/* ══════════════════════════════════════
            HERO — light
        ══════════════════════════════════════ */}
        <section className="relative bg-white pt-28 md:pt-32 pb-20 md:pb-24 overflow-hidden border-b border-zinc-200">
          <div className="absolute inset-0 bg-grid-pattern opacity-70 pointer-events-none" />
          <div className="absolute right-0 top-0 w-2/3 h-2/3 bg-gradient-to-bl from-electric/10 via-transparent to-transparent pointer-events-none" />

          <div className="container relative">
            {/* Breadcrumb */}
            <nav
              aria-label="Breadcrumb"
              className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 mb-8"
            >
              <Link to="/" className="hover:text-electric transition-colors">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/app-categories" className="hover:text-electric transition-colors">App Categories</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-charcoal">{cat.name}</span>
            </nav>

            <div className="grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-3 py-1.5 mb-6">
                  <span className="text-lg leading-none" aria-hidden>{cat.emoji}</span>
                  <span className="text-overline text-electric">{cat.heroEyebrow}</span>
                </div>

                <motion.h1
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="font-display text-4xl sm:text-5xl lg:text-[64px] font-extrabold leading-[1.05] tracking-tight text-balance text-charcoal"
                  data-testid="category-headline"
                >
                  {cat.heroHeadline}
                </motion.h1>

                {cat.heroAccent && (
                  <p className="mt-4 font-display text-2xl md:text-3xl font-bold text-charcoal leading-tight">
                    <span className="relative inline-block">
                      <span className="relative z-10">{cat.heroAccent}</span>
                      <span className="absolute inset-x-0 bottom-1 h-3 bg-electric/30 -z-0" />
                    </span>
                  </p>
                )}

                <p className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-2xl">
                  {cat.heroTagline}
                </p>

                {/* CTAs */}
                <div className="mt-9 flex flex-wrap items-center gap-3">
                  <Link
                    to="/free-aso-audit"
                    className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold hover:bg-[#42A5F5] hover:-translate-y-0.5 transition-all shadow-lg rounded-xl"
                  >
                    Get Free ASO Audit <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/case-studies"
                    className="inline-flex items-center gap-2 bg-white border border-charcoal text-charcoal px-6 py-3.5 font-semibold hover:bg-charcoal hover:text-white transition-colors"
                  >
                    See {cat.shortName} Results <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* Hero Stats */}
                <div className="mt-10 grid grid-cols-3 gap-3 sm:gap-4 max-w-2xl">
                  {cat.heroStats.map((s) => (
                    <div
                      key={s.label}
                      className="border border-zinc-200 bg-white px-4 py-4"
                    >
                      <p className="font-display text-2xl md:text-3xl font-extrabold text-charcoal">
                        {s.value}
                      </p>
                      <p className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1 leading-snug">
                        {s.label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Cover image */}
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="lg:col-span-5"
              >
                <div className="relative">
                  <div className="absolute -inset-2 bg-electric/10 -z-10" />
                  <div className="aspect-[4/3] overflow-hidden border border-zinc-200 bg-zinc-100">
                    <img
                      src={cat.cover}
                      alt={`${cat.name} App Store Optimization`}
                      className="w-full h-full object-cover"
                      loading="eager"
                      width="640"
                      height="480"
                    />
                  </div>
                  <div className="absolute -bottom-4 -right-4 bg-electric text-white px-4 py-3 shadow-lg rounded-xl">
                    <p className="text-[10px] uppercase tracking-widest opacity-80">Category</p>
                    <p className="font-display font-bold text-lg">{cat.shortName}</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            INSIGHTS — Why this category is different
        ══════════════════════════════════════ */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
              <div className="lg:col-span-8">
                <p className="text-overline text-electric mb-3">01 — Why it's different</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                  Why {cat.shortName} ASO needs its own playbook.
                </h2>
              </div>
              <p className="lg:col-span-4 text-zinc-600 leading-relaxed">
                Generic ASO doesn't rank a {cat.shortName.toLowerCase()} app. Three specific
                things matter more than the rest — here they are.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
              {cat.insightStats.map((s, idx) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.08 }}
                  className="bg-white p-7 md:p-8 flex flex-col"
                >
                  <div className="flex items-baseline gap-1 mb-3">
                    <span className="font-display text-5xl md:text-6xl font-extrabold text-charcoal leading-none">
                      {s.value}
                    </span>
                  </div>
                  <p className="font-display font-bold text-charcoal text-base leading-snug mb-2">
                    {s.label}
                  </p>
                  <p className="text-sm text-zinc-600 leading-relaxed">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            PILLARS — The 6-pillar ASO playbook
        ══════════════════════════════════════ */}
        <section className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-200">
          <div className="container">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
              <div className="lg:col-span-8">
                <p className="text-overline text-electric mb-3">02 — Our playbook</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                  The ASO playbook we run for {cat.shortName.toLowerCase()} apps.
                </h2>
              </div>
              <p className="lg:col-span-4 text-zinc-600 leading-relaxed">
                Six pillars, one operating system. Every {cat.shortName.toLowerCase()} engagement runs
                through the same 6-step ranking machine.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
              {cat.pillars.map((p, idx) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                  className="bg-white p-7 md:p-8 hover:bg-charcoal hover:text-white transition-colors group"
                >
                  <div className="flex items-center justify-between mb-5">
                    <span className="font-display font-extrabold text-2xl text-electric">
                      0{idx + 1}
                    </span>
                    <ArrowUpRight className="w-5 h-5 text-zinc-300 group-hover:text-electric transition-colors" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-charcoal group-hover:text-white leading-snug mb-3">
                    {p.title}
                  </h3>
                  <p className="text-sm text-zinc-600 group-hover:text-zinc-300 leading-relaxed">
                    {p.body}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            CASE STUDY + RESULTS
        ══════════════════════════════════════ */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
              <div className="lg:col-span-8">
                <p className="text-overline text-electric mb-3">03 — Proof</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                  Results we've shipped for {cat.shortName.toLowerCase()} apps.
                </h2>
              </div>
              <p className="lg:col-span-4 text-zinc-600 leading-relaxed">
                Real metrics. Real apps. Real markets. Every number below is from a live engagement.
              </p>
            </div>

            <div className="grid lg:grid-cols-12 gap-px bg-zinc-200 border border-zinc-200">
              {/* Featured case study */}
              <motion.article
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45 }}
                className="lg:col-span-7 bg-charcoal text-white p-8 md:p-12 flex flex-col justify-between"
              >
                <div>
                  <span className="inline-block text-overline text-electric mb-5">
                    {cat.caseStudy.badge}
                  </span>
                  <p className="font-display text-7xl md:text-8xl font-extrabold leading-none text-white tracking-tight">
                    {cat.caseStudy.metric}
                  </p>
                  <p className="mt-2 text-zinc-400 text-sm uppercase tracking-widest">
                    {cat.caseStudy.metricLabel}
                  </p>
                  <h3 className="mt-8 font-display text-2xl md:text-3xl font-bold leading-snug">
                    {cat.caseStudy.title}
                  </h3>
                  <p className="mt-4 text-zinc-300 leading-relaxed text-[15px]">
                    {cat.caseStudy.body}
                  </p>
                </div>
                <Link
                  to="/case-studies"
                  className="mt-8 inline-flex items-center gap-2 text-electric font-semibold hover:gap-3 transition-all w-fit"
                >
                  Read full case study <ArrowUpRight className="w-4 h-4" />
                </Link>
              </motion.article>

              {/* Results stack */}
              <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-px bg-zinc-200">
                {cat.results.map((r, idx) => (
                  <motion.div
                    key={r.label}
                    initial={{ opacity: 0, x: 16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: idx * 0.07 }}
                    className="bg-white p-6 md:p-7"
                  >
                    <p className="font-display text-3xl md:text-4xl font-extrabold text-charcoal leading-none">
                      {r.metric}
                    </p>
                    <p className="mt-2 text-charcoal font-semibold text-sm">
                      {r.label}
                    </p>
                    <p className="mt-1 text-xs text-zinc-500 uppercase tracking-widest">
                      {r.market}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            KEYWORDS — dark
        ══════════════════════════════════════ */}
        <section className="py-20 md:py-28 bg-charcoal text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern-dark opacity-[0.06] pointer-events-none" />
          <div className="container relative">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
              <div className="lg:col-span-8">
                <p className="text-overline text-electric mb-3">04 — Keyword universe</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-balance leading-[1.05]">
                  Keywords we rank {cat.shortName.toLowerCase()} apps for.
                </h2>
              </div>
              <p className="lg:col-span-4 text-zinc-400 leading-relaxed">
                A snapshot from our live keyword stack. Each engagement gets 50–100 mapped
                terms, clustered by intent and competitor overlap.
              </p>
            </div>

            <div className="flex flex-wrap gap-2.5">
              {cat.keywords.map((kw, idx) => (
                <motion.span
                  key={kw}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: idx * 0.03 }}
                  className="inline-flex items-center gap-1.5 border border-white/20 bg-white/5 backdrop-blur-sm px-4 py-2 text-sm font-mono text-zinc-200 hover:border-electric hover:bg-electric/10 hover:text-white transition-colors"
                >
                  <span className="w-1 h-1 bg-electric" />
                  {kw}
                </motion.span>
              ))}
            </div>

            <div className="mt-10 inline-flex items-center gap-2 text-sm text-zinc-400">
              <Sparkles className="w-4 h-4 text-electric" />
              <span>+ 40–80 more keywords mapped per app, depending on category breadth.</span>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            PROCESS — 5 steps
        ══════════════════════════════════════ */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
              <div className="lg:col-span-8">
                <p className="text-overline text-electric mb-3">05 — Process</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                  How we ship #1 ranks — 5 steps.
                </h2>
              </div>
              <p className="lg:col-span-4 text-zinc-600 leading-relaxed">
                The same operating cadence we've used to rank 320+ apps across 15+ markets.
                Audit. Strategy. Optimize. Test. Scale.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-zinc-200 border border-zinc-200">
              {PROCESS_STEPS.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={step.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: idx * 0.06 }}
                    className="bg-white p-6 md:p-7 flex flex-col"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <span className="font-mono text-xs text-electric tracking-widest">
                        {step.n}
                      </span>
                      <Icon className="w-5 h-5 text-charcoal" strokeWidth={2} />
                    </div>
                    <h3 className="font-display text-lg font-bold text-charcoal mb-2">
                      {step.title}
                    </h3>
                    <p className="text-sm text-zinc-600 leading-relaxed">{step.text}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            FAQ
        ══════════════════════════════════════ */}
        <section className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-200">
          <div className="container grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <p className="text-overline text-electric mb-3">06 — FAQ</p>
              <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                Frequently asked about {cat.shortName} ASO.
              </h2>
              <p className="mt-5 text-zinc-600 leading-relaxed">
                Got a {cat.shortName.toLowerCase()}-specific question we haven't answered? Drop it in
                the audit form — a real human will reply within 24h.
              </p>

              <Link
                to="/free-aso-audit"
                className="mt-8 inline-flex items-center gap-2 bg-charcoal text-white px-6 py-3 font-semibold hover:bg-electric transition-colors"
              >
                Ask the team <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="lg:col-span-8">
              <FAQAccordion items={cat.faq} />
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            RELATED CATEGORIES
        ══════════════════════════════════════ */}
        {related.length > 0 && (
          <section className="py-20 md:py-28 bg-white">
            <div className="container">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                <div>
                  <p className="text-overline text-electric mb-3">07 — Related categories</p>
                  <h2 className="font-display text-3xl md:text-4xl font-extrabold text-charcoal leading-[1.05]">
                    Other categories we rank.
                  </h2>
                </div>
                <Link
                  to="/app-categories"
                  className="inline-flex items-center gap-2 border border-charcoal text-charcoal px-5 py-2.5 text-sm font-semibold hover:bg-charcoal hover:text-white transition-colors w-fit"
                >
                  View all 50+ categories <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
                {related.map((r, idx) => (
                  <motion.div
                    key={r.slug}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: idx * 0.06 }}
                  >
                    <Link
                      to={`/aso/${r.slug}`}
                      className="bg-white p-6 md:p-7 flex flex-col h-full hover:bg-charcoal hover:text-white transition-colors group"
                    >
                      <span className="text-3xl mb-5" aria-hidden>{r.emoji}</span>
                      <h3 className="font-display text-lg font-bold text-charcoal group-hover:text-white leading-snug">
                        {r.name}
                      </h3>
                      <span className="mt-auto pt-5 inline-flex items-center gap-1 text-xs font-bold text-zinc-400 group-hover:text-electric uppercase tracking-widest">
                        ASO playbook <ArrowUpRight className="w-3.5 h-3.5" />
                      </span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        <CTABanner />
      </main>
      <Footer />
    </>
  );
}
